import { useMemo } from "react";
import { CanvasConfig, CanvasLocation, CanvasPresentation, CanvasType, Plugin } from "types";
import shallow from "zustand/shallow";
import { canvasSizePx } from "./constants";
import { useEmbeddedAppStore } from "./embeddedAppStore";
import { useInstalledPluginsFromStore } from "@/stores/globalStore";

interface PushOffset {
    leftPx: number;
    rightPx: number;
}

type CanvasPlacement = Pick<CanvasConfig, "canvasPresentation" | "canvasLocation" | "canvasSize">;

// Adds a push drawer's width to the side it opens on. Non-push canvases contribute nothing.
const addPushOffset = (offset: PushOffset, placement: CanvasPlacement): PushOffset => {
    if (placement.canvasPresentation !== CanvasPresentation.PUSH) {
        return offset;
    }

    const px = canvasSizePx(placement.canvasSize);
    if (placement.canvasLocation === CanvasLocation.LEFT) {
        return { leftPx: offset.leftPx + px, rightPx: offset.rightPx };
    }
    return { leftPx: offset.leftPx, rightPx: offset.rightPx + px };
};

// Pure: total left/right push-drawer offset for the open canvases. A plugin contributes from its
// static manifest (only while its canvas is open and is a drawer) and from any dynamic canvas.
export const computePushOffset = (
    plugins: Plugin[],
    openCanvases: Map<string, Record<string, unknown>>,
    dynamicCanvases: Map<string, CanvasConfig>,
): PushOffset => {
    let offset: PushOffset = { leftPx: 0, rightPx: 0 };

    for (const plugin of plugins) {
        const ui = plugin.ui;
        if (ui && openCanvases.has(plugin.id) && ui.canvasType === CanvasType.DRAWER) {
            offset = addPushOffset(offset, ui);
        }

        const dynamic = dynamicCanvases.get(plugin.id);
        if (dynamic) {
            offset = addPushOffset(offset, dynamic);
        }
    }

    return offset;
};

export const usePushDrawerOffset = () => {
    const plugins = useInstalledPluginsFromStore();
    const { openCanvases, dynamicCanvases } = useEmbeddedAppStore(
        (state) => ({ openCanvases: state.openCanvases, dynamicCanvases: state.dynamicCanvases }),
        shallow,
    );

    return useMemo(
        () => computePushOffset(plugins, openCanvases, dynamicCanvases),
        [plugins, openCanvases, dynamicCanvases],
    );
};
